import {
  Play,
  Square,
  Download,
  Upload,
  Edit3,
  GitBranch,
  Split,
  Repeat,
  Zap,
  Layers,
  ShieldAlert,
  CornerDownLeft,
  LogOut,
  SkipForward,
  // AI Agent Icons
  Brain,
  MessageSquare,
  Wrench,
  Database,
  Globe,
  FileJson,
  Settings,
  // Contract Flow Icons (ADO #242)
  Cog,
  HardHat,
  FunctionSquare,
  // ADO #303 — Instantiate node (Java OOP)
  Sparkles,
  // ADO #304 — Throw node (Java OOP)
  AlertTriangle,
  // ADO #438 — File I/O node (Python with open())
  FileText,
  SquareDashed,
} from 'lucide-react';

// ============================================
// PROGRAMMING TRACK NODE TYPES
// ============================================
export const NODE_TYPES = {
  start: {
    label: 'Start',
    icon: Play,
    bgColor: 'bg-gradient-to-br from-emerald-500 to-teal-500',
    glowColor: 'rgba(16, 185, 129, 0.5)',
    category: 'flow',
    shape: 'circle'
  },
  end: {
    label: 'End',
    icon: Square,
    bgColor: 'bg-gradient-to-br from-rose-500 to-red-500',
    glowColor: 'rgba(244, 63, 94, 0.5)',
    category: 'flow',
    shape: 'circle'
  },
  input: {
    label: 'Input',
    icon: Download,
    bgColor: 'bg-gradient-to-br from-blue-500 to-violet-600',
    glowColor: 'rgba(59, 130, 246, 0.5)',
    category: 'data',
    description: 'Read data from user',
    shape: 'rounded'
  },
  output: {
    label: 'Output',
    icon: Upload,
    bgColor: 'bg-gradient-to-br from-cyan-500 to-blue-500',
    glowColor: 'rgba(6, 182, 212, 0.5)',
    category: 'data',
    description: 'Display data to user',
    shape: 'rounded'
  },
  assignment: {
    label: 'Assignment',
    icon: Edit3,
    bgColor: 'bg-gradient-to-br from-violet-500 to-purple-600',
    glowColor: 'rgba(139, 92, 246, 0.5)',
    category: 'logic',
    description: 'Assign values to variables',
    shape: 'rounded'
  },
  decision: {
    label: 'Decision',
    icon: GitBranch,
    bgColor: 'bg-gradient-to-br from-amber-500 to-orange-500',
    glowColor: 'rgba(245, 158, 11, 0.5)',
    category: 'logic',
    description: 'Branch based on condition',
    shape: 'diamond'
  },
  // ADO #405 — switch / case / default. Multi-way branch on a single value.
  // Reuses the decision `outcomes` data shape (each non-default outcome is a
  // case) so it shares the branch rendering; teal to distinguish from Decision.
  switch: {
    label: 'Switch',
    icon: Split,
    bgColor: 'bg-gradient-to-br from-teal-500 to-cyan-600',
    glowColor: 'rgba(20, 184, 166, 0.5)',
    category: 'logic',
    description: 'Branch on a value (case / default)',
    shape: 'diamond'
  },
  loop: {
    label: 'Loop',
    icon: Repeat,
    bgColor: 'bg-gradient-to-br from-fuchsia-500 to-pink-500',
    glowColor: 'rgba(217, 70, 239, 0.5)',
    category: 'logic',
    description: 'Repeat steps',
    shape: 'rounded'
  },
  action: {
    label: 'Action',
    icon: Zap,
    bgColor: 'bg-gradient-to-br from-yellow-400 to-amber-500',
    glowColor: 'rgba(250, 204, 21, 0.5)',
    category: 'logic',
    description: 'Perform an action',
    shape: 'rounded'
  },
  subflow: {
    label: 'Subflow',
    icon: Layers,
    bgColor: 'bg-gradient-to-br from-slate-400 to-slate-600',
    glowColor: 'rgba(148, 163, 184, 0.5)',
    category: 'flow',
    description: 'Call another flow',
    shape: 'rounded'
  },
  try_catch: {
    label: 'Try-Catch',
    icon: ShieldAlert,
    bgColor: 'bg-gradient-to-br from-red-500 to-orange-500',
    glowColor: 'rgba(239, 68, 68, 0.5)',
    category: 'logic',
    description: 'Handle exceptions safely',
    shape: 'rounded'
  },
  return: {
    label: 'Return',
    icon: CornerDownLeft,
    bgColor: 'bg-gradient-to-br from-rose-500 to-pink-500',
    glowColor: 'rgba(244, 63, 94, 0.5)',
    category: 'flow',
    description: 'Return a value from the function',
    shape: 'rounded',
  },
  placeholder: {
    label: 'Step to choose',
    icon: SquareDashed,
    bgColor: 'bg-slate-400',
    glowColor: 'rgba(148, 163, 184, 0.4)',
    category: 'flow',
    description: 'A step still to choose',
    shape: 'rounded',
  },
  break: {
    label: 'Break',
    icon: LogOut,
    bgColor: 'bg-gradient-to-br from-amber-500 to-orange-500',
    glowColor: 'rgba(245, 158, 11, 0.5)',
    category: 'flow',
    description: 'Leave the loop now → When done',
    shape: 'rounded',
  },
  continue: {
    label: 'Continue',
    icon: SkipForward,
    bgColor: 'bg-gradient-to-br from-sky-500 to-cyan-500',
    glowColor: 'rgba(14, 165, 233, 0.5)',
    category: 'flow',
    description: 'Skip to next iteration ↑ Loop',
    shape: 'rounded',
  },

  // ADO #303 — Java OOP: visual "new ClassName(args)" gesture. Writes the
  // result into a class-instance resource declared via ADO #305. Java-only;
  // injected into the palette by JavaTrackProvider, not by the shared
  // ELEMENT_CATEGORIES list below.
  instantiate: {
    label: 'New Object',
    icon: Sparkles,
    bgColor: 'bg-gradient-to-br from-lime-500 to-green-600',
    glowColor: 'rgba(132, 204, 22, 0.5)',
    category: 'oop',
    description: 'Create a new instance of a class',
    shape: 'rounded',
  },

  // ADO #304 — Java OOP: visual "throw new ExceptionType(msg)" gesture.
  // Pairs with try_catch as the "raise" half of exception handling. Terminal
  // statement — control does not continue past Throw in its branch (see
  // branchTerminates() below). Java-only; injected into the palette by
  // JavaTrackProvider, not by the shared ELEMENT_CATEGORIES list.
  throw: {
    label: 'Throw',
    icon: AlertTriangle,
    bgColor: 'bg-gradient-to-br from-orange-500 to-red-600',
    glowColor: 'rgba(249, 115, 22, 0.5)',
    category: 'logic',
    description: 'Raise an exception',
    shape: 'rounded',
  },

  // ADO #438 — Python File I/O node. Emits a self-contained `with open(...) as f:`
  // read/write block. Python-only; surfaced by PythonTrackProvider (Data category).
  file_io: {
    label: 'File I/O',
    icon: FileText,
    bgColor: 'bg-gradient-to-br from-teal-500 to-cyan-600',
    glowColor: 'rgba(20, 184, 166, 0.5)',
    category: 'data',
    description: 'Read from / write to a file (with open())',
    shape: 'rounded',
  },

  // ADO #242 — Contract Flow nodes. These appear only in Contract Flow
  // tabs (flowType === 'contract'). Implementation flow tabs hide them.
  configuration: {
    label: 'Configuration',
    icon: Cog,
    bgColor: 'bg-gradient-to-br from-slate-500 to-zinc-600',
    glowColor: 'rgba(113, 113, 122, 0.5)',
    category: 'contract',
    description: 'Class access, modifiers, extends, implements',
    shape: 'rounded',
    contractOnly: true,
    maxPerFlow: 1,
  },
  constructor_decl: {
    label: 'Constructor',
    icon: HardHat,
    bgColor: 'bg-gradient-to-br from-indigo-500 to-blue-600',
    glowColor: 'rgba(99, 102, 241, 0.5)',
    category: 'contract',
    description: 'Declare a constructor — creates an implementation tab',
    shape: 'rounded',
    contractOnly: true,
    createsImplTab: true,
  },
  method_decl: {
    label: 'Method',
    icon: FunctionSquare,
    bgColor: 'bg-gradient-to-br from-teal-500 to-emerald-600',
    glowColor: 'rgba(20, 184, 166, 0.5)',
    category: 'contract',
    description: 'Declare a method — creates an implementation tab',
    shape: 'rounded',
    contractOnly: true,
    createsImplTab: true,
  },
  inner_class_decl: {
    label: 'Inner Class',
    icon: Layers,
    bgColor: 'bg-gradient-to-br from-violet-500 to-purple-600',
    glowColor: 'rgba(139, 92, 246, 0.5)',
    category: 'contract',
    description: 'Declare a nested class — creates a new session',
    shape: 'rounded',
    contractOnly: true,
    createsSession: true,
  },
  // ADO #243 — Java Record only, max 1, params auto from record fields
  compact_constructor_decl: {
    label: 'Compact Constructor',
    icon: HardHat,
    bgColor: 'bg-gradient-to-br from-pink-500 to-rose-600',
    glowColor: 'rgba(236, 72, 153, 0.5)',
    category: 'contract',
    description: 'Validation/normalization for record components — creates an implementation tab',
    shape: 'rounded',
    contractOnly: true,
    createsImplTab: true,
    recordOnly: true,
    maxPerFlow: 1,
  },
  // ADO #243 — Java Class only, max 1
  static_init_decl: {
    label: 'Static Initializer',
    icon: Zap,
    bgColor: 'bg-gradient-to-br from-amber-500 to-orange-600',
    glowColor: 'rgba(245, 158, 11, 0.5)',
    category: 'contract',
    description: 'Runs once when the class is loaded — creates an implementation tab',
    shape: 'rounded',
    contractOnly: true,
    createsImplTab: true,
    classOnly: true,
    maxPerFlow: 1,
  },
  // ADO #244 — C track (and later C++ free functions / Python module functions).
  // Function declaration in Contract Flow creates an implementation tab.
  function_decl: {
    label: 'Function',
    icon: FunctionSquare,
    bgColor: 'bg-gradient-to-br from-cyan-500 to-sky-600',
    glowColor: 'rgba(6, 182, 212, 0.5)',
    category: 'contract',
    description: 'Declare a function — creates an implementation tab',
    shape: 'rounded',
    contractOnly: true,
    createsImplTab: true,
  },
  // ADO #244 Phase 3b — C++ destructor (max 1 per class, auto-created on
  // session creation per OQ5 Option A; user can delete + re-add via palette).
  destructor_decl: {
    label: 'Destructor',
    icon: HardHat,
    bgColor: 'bg-gradient-to-br from-rose-500 to-red-600',
    glowColor: 'rgba(244, 63, 94, 0.5)',
    category: 'contract',
    description: 'Cleanup logic — runs on object destruction',
    shape: 'rounded',
    contractOnly: true,
    createsImplTab: true,
    cppOnly: true,
    maxPerFlow: 1,
  },
  // ADO #244 Phase 3c — C++ operator overload (multiple allowed).
  operator_decl: {
    label: 'Operator',
    icon: FunctionSquare,
    bgColor: 'bg-gradient-to-br from-fuchsia-500 to-purple-600',
    glowColor: 'rgba(217, 70, 239, 0.5)',
    category: 'contract',
    description: 'Operator overload — creates an implementation tab',
    shape: 'rounded',
    contractOnly: true,
    createsImplTab: true,
    cppOnly: true,
  },
};

// ============================================
// CODE NEXT — NODE-TYPE COLOR MAP
// Used for color-coded highlights matching node card ↔ code lines
// ============================================
export const CODE_NEXT_COLORS = {
  input:      { ring: 'ring-blue-400',    ringDone: 'ring-blue-500',    css: 'rgba(59,130,246,0.18)',  border: '#3b82f6',  label: 'blue'    },
  output:     { ring: 'ring-cyan-400',    ringDone: 'ring-cyan-500',    css: 'rgba(6,182,212,0.18)',   border: '#06b6d4',  label: 'cyan'    },
  assignment: { ring: 'ring-violet-400',  ringDone: 'ring-violet-500',  css: 'rgba(139,92,246,0.18)',  border: '#8b5cf6',  label: 'violet'  },
  decision:   { ring: 'ring-amber-400',   ringDone: 'ring-amber-500',   css: 'rgba(245,158,11,0.18)', border: '#f59e0b',  label: 'amber'   },
  switch:     { ring: 'ring-teal-400',    ringDone: 'ring-teal-500',    css: 'rgba(20,184,166,0.18)', border: '#14b8a6',  label: 'teal'    },
  loop:       { ring: 'ring-pink-400',    ringDone: 'ring-pink-500',    css: 'rgba(236,72,153,0.18)', border: '#ec4899',  label: 'pink'    },
  action:     { ring: 'ring-yellow-400',  ringDone: 'ring-yellow-500',  css: 'rgba(250,204,21,0.18)', border: '#facc15',  label: 'yellow'  },
  subflow:    { ring: 'ring-slate-400',   ringDone: 'ring-slate-500',   css: 'rgba(148,163,184,0.18)',border: '#94a3b8',  label: 'slate'   },
  start:      { ring: 'ring-emerald-400', ringDone: 'ring-emerald-500', css: 'rgba(16,185,129,0.18)', border: '#10b981',  label: 'emerald' },
  end:        { ring: 'ring-rose-400',    ringDone: 'ring-rose-500',    css: 'rgba(244,63,94,0.18)',  border: '#f43f5e',  label: 'rose'    },
  try_catch:  { ring: 'ring-red-400',     ringDone: 'ring-red-500',     css: 'rgba(239,68,68,0.18)',  border: '#ef4444',  label: 'red'     },
  return:     { ring: 'ring-rose-400',    ringDone: 'ring-rose-500',    css: 'rgba(244,63,94,0.18)',  border: '#f43f5e',  label: 'rose'    },
  break:      { ring: 'ring-amber-400',   ringDone: 'ring-amber-500',   css: 'rgba(245,158,11,0.18)', border: '#f59e0b',  label: 'amber'   },
  continue:   { ring: 'ring-sky-400',     ringDone: 'ring-sky-500',     css: 'rgba(14,165,233,0.18)', border: '#0ea5e9',  label: 'sky'     },
  // ADO #303 — Instantiate (Java OOP)
  instantiate:{ ring: 'ring-lime-400',    ringDone: 'ring-lime-500',    css: 'rgba(132,204,22,0.18)', border: '#84cc16',  label: 'lime'    },
  // ADO #304 — Throw (Java OOP). Orange-forward to differentiate from
  // try_catch's red-forward palette: throw "raises" outbound, catch "handles" inbound.
  throw:      { ring: 'ring-orange-400',  ringDone: 'ring-orange-500',  css: 'rgba(249,115,22,0.18)', border: '#f97316',  label: 'orange'  },
  file_io:    { ring: 'ring-teal-400',    ringDone: 'ring-teal-500',    css: 'rgba(20,184,166,0.18)', border: '#14b8a6',  label: 'teal'    },
};
// Fallback for unknown types
CODE_NEXT_COLORS._default = { ring: 'ring-emerald-400', ringDone: 'ring-emerald-500', css: 'rgba(16,185,129,0.18)', border: '#10b981', label: 'emerald' };

// ============================================
// AI AGENT TRACK NODE TYPES
// ============================================
export const AI_NODE_TYPES = {
  start: {
    label: 'Start',
    icon: Play,
    bgColor: 'bg-gradient-to-br from-emerald-500 to-teal-500',
    glowColor: 'rgba(16, 185, 129, 0.5)',
    category: 'flow',
    shape: 'circle'
  },
  end: {
    label: 'End',
    icon: Square,
    bgColor: 'bg-gradient-to-br from-rose-500 to-red-500',
    glowColor: 'rgba(244, 63, 94, 0.5)',
    category: 'flow',
    shape: 'circle'
  },
  llm_call: {
    label: 'LLM Call',
    icon: Brain,
    bgColor: 'bg-gradient-to-br from-violet-500 to-purple-600',
    glowColor: 'rgba(139, 92, 246, 0.5)',
    category: 'ai',
    description: 'Call a language model (GPT, Claude, Gemini)',
    shape: 'rounded'
  },
  prompt_template: {
    label: 'Prompt',
    icon: MessageSquare,
    bgColor: 'bg-gradient-to-br from-blue-500 to-cyan-500',
    glowColor: 'rgba(59, 130, 246, 0.5)',
    category: 'ai',
    description: 'Build dynamic prompts with variables',
    shape: 'rounded'
  },
  tool_call: {
    label: 'Tool',
    icon: Wrench,
    bgColor: 'bg-gradient-to-br from-amber-500 to-orange-500',
    glowColor: 'rgba(245, 158, 11, 0.5)',
    category: 'ai',
    description: 'Define and execute tools/functions',
    shape: 'rounded'
  },
  memory: {
    label: 'Memory',
    icon: Database,
    bgColor: 'bg-gradient-to-br from-green-500 to-emerald-500',
    glowColor: 'rgba(34, 197, 94, 0.5)',
    category: 'ai',
    description: 'Store and retrieve conversation context',
    shape: 'rounded'
  },
  api_call: {
    label: 'API Call',
    icon: Globe,
    bgColor: 'bg-gradient-to-br from-indigo-500 to-blue-600',
    glowColor: 'rgba(99, 102, 241, 0.5)',
    category: 'integration',
    description: 'Call external APIs',
    shape: 'rounded'
  },
  json_parser: {
    label: 'JSON Parse',
    icon: FileJson,
    bgColor: 'bg-gradient-to-br from-slate-500 to-gray-600',
    glowColor: 'rgba(100, 116, 139, 0.5)',
    category: 'data',
    description: 'Parse and extract JSON data',
    shape: 'rounded'
  },
  decision: {
    label: 'Decision',
    icon: GitBranch,
    bgColor: 'bg-gradient-to-br from-yellow-500 to-amber-500',
    glowColor: 'rgba(234, 179, 8, 0.5)',
    category: 'logic',
    description: 'Branch based on LLM output or conditions',
    shape: 'diamond'
  },
  loop: {
    label: 'Loop',
    icon: Repeat,
    bgColor: 'bg-gradient-to-br from-fuchsia-500 to-pink-500',
    glowColor: 'rgba(217, 70, 239, 0.5)',
    category: 'logic',
    description: 'Retry or iterate over data',
    shape: 'rounded'
  },
  config: {
    label: 'Config',
    icon: Settings,
    bgColor: 'bg-gradient-to-br from-gray-500 to-slate-600',
    glowColor: 'rgba(107, 114, 128, 0.5)',
    category: 'setup',
    description: 'Configure LLM provider settings',
    shape: 'rounded'
  }
};

// ============================================
// ELEMENT CATEGORIES BY TRACK TYPE
// ============================================
export const ELEMENT_CATEGORIES = [
  { name: 'LOGIC', items: ['assignment', 'decision', 'switch', 'loop', 'action', 'try_catch'] },
  { name: 'DATA', items: ['input', 'output'] },
  { name: 'FLOW', items: ['subflow', 'return'] }
];

// ADO #242 — Contract Flow has its own node palette. These categories
// replace ELEMENT_CATEGORIES when the active flow is a contract tab.
export const CONTRACT_ELEMENT_CATEGORIES = [
  { name: 'DECLARATION', items: ['configuration', 'constructor_decl', 'method_decl'] },
];

export const AI_ELEMENT_CATEGORIES = [
  { name: 'AI', items: ['llm_call', 'prompt_template', 'tool_call', 'memory'] },
  { name: 'LOGIC', items: ['decision', 'loop'] },
  { name: 'INTEGRATION', items: ['api_call', 'json_parser'] },
  { name: 'SETUP', items: ['config'] }
];

// ============================================
// HELPER FUNCTIONS
// ============================================
export const generateId = () => `node-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

/**
 * Check if a branch terminates — its last node is End, Return, or a
 * fully-terminating Decision (all sub-branches terminate). Used by
 * DecisionBranches (canvas rendering) and codeGenerator (skip unreachable code).
 */
export function branchTerminates(nodes) {
  if (!nodes || nodes.length === 0) return false;
  const last = nodes[nodes.length - 1];
  // ADO #304 — Throw is terminal (control transfers to the catch site), so
  // a Decision branch ending in Throw counts as a complete terminating path.
  if (last.type === 'end' || last.type === 'return' || last.type === 'break' || last.type === 'continue' || last.type === 'throw') return true;
  if (last.type === 'decision' || last.type === 'switch') {
    const outcomes = last.outcomes || [];
    // A switch only fully terminates if it has a default branch (otherwise an
    // unmatched value falls through the whole statement).
    const covered = last.type === 'decision' || outcomes.some(o => o.isDefault);
    return outcomes.length > 0 && covered && outcomes.every(o => branchTerminates(o.nodes || []));
  }
  return false;
}

// Get node types based on track type
export const getNodeTypes = (trackType) => {
  return trackType === 'ai_agent' ? AI_NODE_TYPES : NODE_TYPES;
};

// Get element categories based on track type and flow type.
// ADO #242: contract flows get their own palette.
export const getElementCategories = (trackType, flowType) => {
  if (flowType === 'contract') return CONTRACT_ELEMENT_CATEGORIES;
  return trackType === 'ai_agent' ? AI_ELEMENT_CATEGORIES : ELEMENT_CATEGORIES;
};
